import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface AvatarGroupUser {
  id: number;
  name: string;
  surname: string;
}

interface AvatarGroupProps {
  users: AvatarGroupUser[];
  maxDisplay?: number;
  onUserClick?: (userId: number) => void;
}

const COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-teal-100 text-teal-700',
];

function getColorForUser(userId: number): string {
  return COLORS[userId % COLORS.length];
}

function Avatar({
  user,
  isFirst,
  clickable,
  onClick,
}: {
  user: AvatarGroupUser;
  isFirst: boolean;
  clickable: boolean;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLButtonElement>(null);

  function handleMouseEnter() {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setTooltipPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
    setHovered(true);
  }

  return (
    <>
      <button
        ref={ref}
        className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium border-2 border-background ${getColorForUser(user.id)} ${!isFirst ? '-ml-2' : ''} ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-primary/30' : 'cursor-default'}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
      >
        {user.name[0]}
        {user.surname[0]}
      </button>
      {hovered &&
        createPortal(
          <div
            className="fixed px-2 py-1 rounded bg-foreground text-background text-xs whitespace-nowrap pointer-events-none z-[100] -translate-x-1/2 -translate-y-full"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            {user.name} {user.surname}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
          </div>,
          document.body,
        )}
    </>
  );
}

export function AvatarGroup({
  users,
  maxDisplay = 5,
  onUserClick,
}: AvatarGroupProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const displayed = users.slice(0, maxDisplay);
  const overflow = users.slice(maxDisplay);

  useEffect(() => {
    if (!popoverOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  function togglePopover(e: React.MouseEvent) {
    e.stopPropagation();
    if (!popoverOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 8,
        left: rect.right - 192,
      });
    }
    setPopoverOpen(!popoverOpen);
  }

  return (
    <div className="flex items-center">
      {displayed.map((user, index) => (
        <Avatar
          key={user.id}
          user={user}
          isFirst={index === 0}
          clickable={!!onUserClick}
          onClick={() => onUserClick?.(user.id)}
        />
      ))}
      {overflow.length > 0 && (
        <>
          <button
            ref={buttonRef}
            className="-ml-2 h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground border-2 border-background hover:bg-muted/80 cursor-pointer"
            onClick={togglePopover}
          >
            +{overflow.length}
          </button>
          {popoverOpen &&
            createPortal(
              <div
                ref={popoverRef}
                className="fixed w-48 max-h-60 overflow-y-auto rounded-lg border bg-background shadow-lg z-[100]"
                style={{ top: popoverPos.top, left: popoverPos.left }}
              >
                <div className="py-1">
                  {overflow.map((user) => (
                    <button
                      key={user.id}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPopoverOpen(false);
                        onUserClick?.(user.id);
                      }}
                    >
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium shrink-0 ${getColorForUser(user.id)}`}
                      >
                        {user.name[0]}
                        {user.surname[0]}
                      </div>
                      <span className="truncate">
                        {user.name} {user.surname}
                      </span>
                    </button>
                  ))}
                </div>
              </div>,
              document.body,
            )}
        </>
      )}
    </div>
  );
}

export type { AvatarGroupUser };
